import { useMemo } from "react";
import { BookOpen, ClipboardCheck, ExternalLink, Target } from "lucide-react";
import SlideGuide, {
  type GuideSlide,
  type GuideStep,
} from "@/components/training/SlideGuide";
import {
  getCurriculumReferences,
  type CurriculumReference,
} from "@/data/curriculumReferences";

export interface CompetencyCourseDefinition {
  id: string;
  name: string;
  trackingName: string;
  canonicalPath: string;
  storageKey: string;
  slideStorageKey: string;
  version: string;
  lastReviewed: string;
  estimatedTime: string;
  totalTime: string;
  prerequisites: string[];
  objectives: string[];
  assessment: {
    title: string;
    status: "planned" | "pilot" | "live";
    evidence: string[];
  };
  sourceIds: string[];
  seo: {
    title: string;
    description: string;
    keywords: string[];
  };
  cover: {
    title: string;
    accent: string;
    description: string;
    finishLineTitle: string;
    finishLineBody: string;
  };
  steps: GuideStep[];
  finish: {
    heading: string;
    body: string;
    nextHref: string;
    nextTitle: string;
    nextBody: string;
  };
}

const buildSlides = (steps: GuideStep[]): GuideSlide[] => {
  const lessonSlides: GuideSlide[] = steps.flatMap((step) => {
    const slides: GuideSlide[] = [
      {
        id: step.id,
        title: step.title,
        kind: "activity",
        tone: "paper",
        stepId: step.id,
        showWhy: true,
        showNote: Boolean(step.note),
        showReference: Boolean(step.reference),
        showDeliverables: Boolean(step.deliverables),
        showLinks: Boolean(step.links),
      },
    ];

    if (step.prompts?.length) {
      slides.push({
        id: `${step.id}-practice`,
        title: `${step.title}: guided practice`,
        heading: "Apply it before you move on",
        kind: "prompt",
        tone: "blue",
        stepId: step.id,
        showNote: Boolean(step.note),
        showDeliverables: Boolean(step.deliverables),
      });
    }

    return slides;
  });

  return [
    { id: "start", title: "Start", kind: "cover", tone: "dark" },
    { id: "objectives", title: "Learning objectives", kind: "outcomes", tone: "paper" },
    { id: "course-orientation", title: "Course requirements", kind: "custom", tone: "blue" },
    { id: "course-roadmap", title: "Course roadmap", kind: "roadmap", tone: "dark" },
    ...lessonSlides,
    { id: "finish", title: "Practice package complete", kind: "finish", tone: "dark" },
  ];
};

const CourseOrientation = ({
  course,
  references,
}: {
  course: CompetencyCourseDefinition;
  references: CurriculumReference[];
}) => (
  <div className="mx-auto flex min-h-full w-full max-w-6xl flex-col justify-center py-8 sm:py-12">
    <p className="text-xs font-extrabold uppercase tracking-[0.18em] text-orange-300">Course requirements</p>
    <h1 className="mt-4 max-w-5xl font-serif text-4xl font-semibold leading-tight tracking-[-0.04em] text-white sm:text-5xl lg:text-6xl">
      Know what you need—and what counts as evidence.
    </h1>
    <div className="mt-8 grid gap-5 lg:grid-cols-2">
      <section className="rounded-2xl border border-white/15 bg-white/[0.06] p-5 sm:p-6" aria-labelledby={`${course.id}-prerequisites`}>
        <div className="flex items-center gap-3 text-orange-300"><BookOpen className="h-5 w-5" /><h2 id={`${course.id}-prerequisites`} className="font-bold text-white">Prerequisites</h2></div>
        <ul className="mt-4 space-y-2 text-sm leading-relaxed text-slate-200">
          {course.prerequisites.map((item) => <li key={item} className="flex gap-2"><span aria-hidden="true">•</span><span>{item}</span></li>)}
        </ul>
      </section>
      <section className="rounded-2xl border border-white/15 bg-white/[0.06] p-5 sm:p-6" aria-labelledby={`${course.id}-evidence`}>
        <div className="flex items-center gap-3 text-orange-300"><ClipboardCheck className="h-5 w-5" /><h2 id={`${course.id}-evidence`} className="font-bold text-white">Assessment connection</h2></div>
        <p className="mt-4 text-sm font-bold text-white">{course.assessment.title} · {course.assessment.status}</p>
        <ul className="mt-3 space-y-2 text-sm leading-relaxed text-slate-200">
          {course.assessment.evidence.map((item) => <li key={item} className="flex gap-2"><span aria-hidden="true">•</span><span>{item}</span></li>)}
        </ul>
        <p className="mt-4 rounded-xl border border-amber-300/25 bg-amber-300/10 p-3 text-xs leading-relaxed text-amber-100">
          Lesson practice is formative. This course does not issue proficiency or certification; the listed performance assessment requires separate evidence and qualified human evaluation.
        </p>
      </section>
    </div>
    <section className="mt-5 rounded-2xl border border-white/15 bg-white/[0.06] p-5 sm:p-6" aria-labelledby={`${course.id}-sources`}>
      <div className="flex items-center gap-3 text-orange-300"><Target className="h-5 w-5" /><h2 id={`${course.id}-sources`} className="font-bold text-white">Authoritative sources and course version</h2></div>
      <p className="mt-3 text-sm text-slate-300">Version {course.version} · estimated {course.estimatedTime} · sources reviewed {course.lastReviewed}</p>
      <div className="mt-4 grid gap-3 sm:grid-cols-2">
        {references.map((reference) => (
          <a key={reference.id} href={reference.url} target="_blank" rel="noopener noreferrer" className="rounded-xl border border-white/10 bg-black/15 p-4 text-left transition-colors hover:border-orange-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-300">
            <span className="flex items-start justify-between gap-3 text-sm font-bold text-white">{reference.title}<ExternalLink className="mt-0.5 h-4 w-4 flex-shrink-0 text-orange-300" /></span>
            <span className="mt-2 block text-xs leading-relaxed text-slate-400">{reference.publisher} · {reference.use}</span>
          </a>
        ))}
      </div>
    </section>
  </div>
);

export const CompetencyCourse = ({ course }: { course: CompetencyCourseDefinition }) => {
  const slides = useMemo(() => buildSlides(course.steps), [course.steps]);
  const references = useMemo(() => getCurriculumReferences(course.sourceIds), [course.sourceIds]);

  return (
    <SlideGuide
      name={course.name}
      trackingName={course.trackingName}
      canonicalPath={course.canonicalPath}
      storageKey={course.storageKey}
      slideStorageKey={course.slideStorageKey}
      seo={course.seo}
      schema={{
        description: course.seo.description,
        totalTime: course.totalTime,
        supplies: ["A recurring workflow or provided practice case", "A place to save the practice evidence"],
      }}
      cover={{
        kicker: "Longhand AI competency course",
        title: course.cover.title,
        accent: course.cover.accent,
        description: course.cover.description,
        badges: [`Version ${course.version}`, course.estimatedTime, "Practice evidence required"],
        finishLineTitle: course.cover.finishLineTitle,
        finishLineBody: course.cover.finishLineBody,
      }}
      outcomes={{
        eyebrow: "Measurable learning objectives",
        heading: "By the end, you can produce evidence—not just repeat the framework.",
        items: course.objectives,
        summary: "You will see a worked example, diagnose a weak example, complete guided practice, and leave with an independent practice artifact. The artifact prepares you for a later human-scored performance assessment.",
      }}
      roadmapHeading="Explain it. See it. Challenge it. Apply it. Save the evidence."
      steps={course.steps}
      slides={slides}
      finish={{
        eyebrow: "Formative practice complete",
        heading: course.finish.heading,
        body: course.finish.body,
        cards: [
          {
            eyebrow: "Next course",
            title: course.finish.nextTitle,
            body: course.finish.nextBody,
            href: course.finish.nextHref,
          },
          {
            eyebrow: "Review your gaps",
            title: "Return to the AI Tune-Up",
            body: "Update your formative self-rating and open the next direct lesson. Green remains self-reported, not certified.",
            href: "/ai-30/program/ai-tune-up",
          },
        ],
      }}
      contentsExtras={[{ slideId: "course-orientation", label: "Requirements and sources" }]}
      renderCustomSlide={(slide) => slide.id === "course-orientation" ? <CourseOrientation course={course} references={references} /> : null}
    />
  );
};

export default CompetencyCourse;
